import type { ComponentProps, ReactNode } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Command as CommandPrimitive, defaultFilter } from "cmdk";
import { useTranslation } from "react-i18next";

import { cn } from "../../../lib/utils";
import { OverlayLayerProvider, useOverlayZIndex } from "../../../lib/overlay-z-index";
import { SelectContentContext, useSelectContext } from "../SelectContext";
import { SelectItemCollector } from "./SelectItemCollector";
import { SelectSearch } from "./SelectSearch";
import { SelectCreateRow } from "../SelectCreateRow";
import { SelectListEmpty } from "./SelectListEmpty";
import { SelectListLoadingMore } from "./SelectListLoadingMore";
import { SelectPinnedItems } from "./SelectPinnedItems";
import { useSelectContent } from "./useSelectContent";

/** What a custom `filterFn` receives for each item. */
export interface SelectFilterItem {
  value: string;
  textValue: string;
  keywords: string[];
}

export type SelectContentProps = Omit<
  ComponentProps<typeof PopoverPrimitive.Content>,
  "children" | "onOpenAutoFocus" | "onCloseAutoFocus" | "onInteractOutside"
> & {
  children?: ReactNode;
  /** Which side of each item the selected checkmark sits on. @default "right" */
  indicatorSide?: "left" | "right";
  /** The search input appears once there are more than this many items. @default 5 */
  searchThreshold?: number;
  /** Force the search input on or off, ignoring `searchThreshold`. External filtering always shows it unless this is `false`. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Shown when no item matches. Defaults to a translated "No results found". */
  emptyText?: ReactNode;
  /**
   * `"internal"` (default): the Select filters and ranks the rendered items against the search text.
   * `"external"`: the Select leaves the items exactly as rendered — filter them yourself from the
   * controlled `search` / `onSearchChange` (in memory or through a query) and render the results.
   */
  filter?: "internal" | "external";
  /** Controlled search text. Reset to `""` through `onSearchChange` whenever the dropdown closes. */
  search?: string;
  onSearchChange?: (search: string) => void;
  /** Internal-filter scorer: return 0 to hide an item, higher to rank it earlier. Defaults to fuzzy matching on each item's text and keywords. */
  filterFn?: (item: SelectFilterItem, search: string) => number;
  /**
   * Shows a loading row in place of the items, so stale results never read as the answer to a new
   * search. While a page requested through `onLoadMore` loads, the items stay and the row follows them.
   */
  loading?: boolean;
  /** Text for the loading row. Defaults to a translated "Loading...". */
  loadingText?: string;
  /** Called when the list is scrolled near its end while `hasMore` is true. */
  onLoadMore?: () => void;
  hasMore?: boolean;
  /** Leave selected items out of the list (the multi-select badges already show them). Otherwise they stay listed with a checkmark. */
  hideSelectedItems?: boolean;
};

export const SelectContent = ({
  className,
  children,
  indicatorSide = "right",
  searchThreshold = 5,
  searchable,
  searchPlaceholder,
  emptyText,
  filter = "internal",
  search,
  onSearchChange,
  filterFn,
  loading = false,
  loadingText,
  onLoadMore,
  hasMore = false,
  hideSelectedItems = false,
  sideOffset = 4,
  onEscapeKeyDown,
  ...props
}: SelectContentProps) => {
  const { t } = useTranslation();
  // Nesting-aware z-index: a select opened inside a dialog stacks above it; see overlay-z-index.tsx.
  const { z, style, className: zClassName } = useOverlayZIndex("popover");
  const {
    highlightedOnOpen,
    showSearch,
    showItems,
    collectItems,
    currentSearch,
    setSearch,
    pendingCommit,
    inputRef,
    commandRef,
    listRef,
    handleOpenAutoFocus,
    handleInteractOutside,
    handleEscapeKeyDown,
    handleCloseAutoFocus,
    handleKeyDown,
    handleListScroll,
  } = useSelectContent({ searchThreshold, searchable, filter, search, onSearchChange, loading, onLoadMore, hasMore });
  const { creator } = useSelectContext();
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("Search...");
  const resolvedLoadingText = loadingText ?? t("Loading...");

  return (
    <SelectContentContext.Provider value={{ hideSelectedItems, setSearch, pendingCommit }}>
      {collectItems && <SelectItemCollector>{children}</SelectItemCollector>}
      <PopoverPrimitive.Portal>
        <OverlayLayerProvider base={z}>
          <PopoverPrimitive.Content
            data-slot="select-content"
            // The listbox inside is the meaningful role; Radix's default "dialog" would wrap it in an unnamed dialog.
            role={undefined}
            style={style}
            sideOffset={sideOffset}
            className={cn(
              "relative flex max-h-[min(300px,var(--radix-popover-content-available-height))] min-w-[max(8rem,var(--radix-popover-trigger-width))] origin-[var(--radix-popover-content-transform-origin)] flex-col overflow-hidden rounded-[10px] border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
              zClassName,
              className
            )}
            onOpenAutoFocus={handleOpenAutoFocus}
            onInteractOutside={handleInteractOutside}
            onEscapeKeyDown={(event) => handleEscapeKeyDown(event, onEscapeKeyDown)}
            onCloseAutoFocus={handleCloseAutoFocus}
            {...props}
          >
            <CommandPrimitive
              ref={commandRef}
              data-indicator-side={indicatorSide}
              label={resolvedSearchPlaceholder}
              shouldFilter={filter === "internal"}
              filter={(value, query, keywords = []) => {
                const [textValue = "", ...itemKeywords] = keywords;
                if (filterFn) return filterFn({ value, textValue, keywords: itemKeywords }, query);
                // Score the item's text, not its value — values are often ids that would match noise.
                return defaultFilter(textValue, query, itemKeywords);
              }}
              defaultValue={highlightedOnOpen}
              onKeyDown={handleKeyDown}
              className="group/select-content flex min-h-0 flex-1 flex-col outline-none"
            >
              {showSearch && (
                <SelectSearch
                  ref={inputRef}
                  value={currentSearch}
                  onValueChange={setSearch}
                  placeholder={resolvedSearchPlaceholder}
                />
              )}
              <CommandPrimitive.List
                ref={listRef}
                className="min-h-0 flex-1 scroll-py-1 overflow-x-hidden overflow-y-auto p-1"
                onScroll={handleListScroll}
                // Inside a modal dialog, the dialog's scroll lock swallows wheel events on this
                // portaled list; stopping them here keeps the list scrollable.
                onWheelCapture={(event) => event.stopPropagation()}
              >
                {showItems && creator && <SelectCreateRow {...(creator === true ? {} : creator)} />}
                {showItems && <SelectPinnedItems />}
                {showItems && children}
                <SelectListLoadingMore loading={loading} loadingText={resolvedLoadingText} />
              </CommandPrimitive.List>
              {/* Outside the list: cmdk re-appends every item to the list's end when it sorts, which would put the creator row below this. */}
              <SelectListEmpty
                loading={loading}
                loadingText={resolvedLoadingText}
                emptyText={emptyText ?? t("No results found")}
              />
            </CommandPrimitive>
          </PopoverPrimitive.Content>
        </OverlayLayerProvider>
      </PopoverPrimitive.Portal>
    </SelectContentContext.Provider>
  );
};
